/**
 * DOMI-CARE - Controlador Principal de la Aplicación
 * Orquesta vistas, ruteo SPA, inicialización offline y estado global.
 */

import './styles/variables.css';
import './styles/reset.css';
import './styles/components.css';
import './styles/main.css';

import { offlineStore } from './api/offlineStore.js';
import { authState } from './state/authState.js';
import { licenseState } from './state/licenseState.js';
import { initTopBar } from './components/navbar.js';
import { renderDemoBanner } from './components/demoBanner.js';
import { SuperAdminManager } from './components/superAdminModal.js';
import { ModalManager } from './components/modal.js';

import { renderPacientesView } from './modules/pacientes/pacientesView.js';
import { renderHCDView } from './modules/historiaClinica/historiaClinicaView.js';
import { renderEscalasView } from './modules/escalas/escalasView.js';
import { renderPrestadoresView } from './modules/prestadores/prestadoresView.js';
import { renderLogisticaView } from './modules/logistica/logisticaView.js';
import { renderAuditoriaView } from './modules/auditoria/auditoriaView.js';
import { renderReportesView } from './modules/reportes/reportesView.js';

class DomiCareApp {
  constructor() {
    this.currentView = 'pacientes';
    this.activePatientId = 'PAC-001';
    this.mainContent = document.getElementById('main-content');
    this.topbar = document.getElementById('app-topbar');
    this.demoBannerContainer = document.getElementById('demo-banner-container');

    this.init();
  }

  async init() {
    try {
      this.mainContent = document.getElementById('main-content');
      this.topbar = document.getElementById('app-topbar');
      this.demoBannerContainer = document.getElementById('demo-banner-container');

      ModalManager.initGlobalListeners();

      // 1. Inicializar Gestor Secreto de SuperAdmin (Atajo Alt+F12 y 5 clics)
      SuperAdminManager.init();

      // 2. Cargar pacientes iniciales
      const pacientes = await offlineStore.getAll('pacientes');
      if (pacientes && pacientes.length > 0) {
        this.activePatientId = pacientes[0].id;
      }

      // 3. Registrar Service Worker para PWA Offline si el navegador lo soporta
      this.registerServiceWorker();

      // 4. Renderizar Banner Demo (si aplica)
      this.updateDemoBanner();

      // 5. Renderizar Topbar
      this.renderHeader(pacientes);

      // 6. Configurar eventos de navegación
      this.setupNavigation();

      // 7. Renderizar vista inicial
      await this.navigateTo(this.currentView);

      // 8. Actualizar UI de usuario en el sidebar
      this.updateSidebarUser();

      // 9. Escuchadores globales
      window.addEventListener('domicare:refresh-views', () => {
        this.navigateTo(this.currentView);
      });

      window.addEventListener('domicare:user-changed', () => {
        this.updateSidebarUser();
        this.navigateTo(this.currentView);
      });

      window.addEventListener('domicare:license-updated', () => {
        this.updateDemoBanner();
        this.renderHeader();
        this.updateSidebarUser();
        this.navigateTo(this.currentView);
      });
    } catch (err) {
      console.error('DOMI-CARE Error en inicialización:', err);
      if (this.mainContent) {
        this.mainContent.innerHTML = `
          <div class="card" style="padding: 2rem; color: var(--danger-600); text-align: center; margin: 2rem;">
            <h3>Error al inicializar DOMI-CARE</h3>
            <p style="margin-top: 0.5rem; color: var(--text-secondary);">${err.message || err}</p>
            <button class="btn btn-primary" onclick="location.reload()" style="margin-top: 1rem;">Reintentar</button>
          </div>
        `;
      }
    }
  }

  updateDemoBanner() {
    if (this.demoBannerContainer) {
      renderDemoBanner(this.demoBannerContainer);
    }
  }

  registerServiceWorker() {
    if ('serviceWorker' in navigator && window.location.protocol.startsWith('http')) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js')
          .then(reg => console.log('DOMI-CARE: Service Worker activo:', reg.scope))
          .catch(err => console.log('DOMI-CARE: SW no disponible en entorno actual:', err));
      });
    }
  }

  async renderHeader(pacientes) {
    if (!pacientes) pacientes = await offlineStore.getAll('pacientes');
    initTopBar(
      this.topbar, 
      pacientes, 
      this.activePatientId,
      (newPatientId) => {
        this.activePatientId = newPatientId;
        this.navigateTo(this.currentView);
      },
      () => {
        this.updateSidebarUser();
        this.navigateTo(this.currentView);
      }
    );
  }

  setupNavigation() {
    const navItems = document.querySelectorAll('.sidebar-nav .nav-item');
    navItems.forEach(item => {
      item.addEventListener('click', () => {
        const view = item.dataset.view;
        if (view) {
          this.navigateTo(view);
        }
      });
    });
  }

  async navigateTo(viewName) {
    this.currentView = viewName;

    // Actualizar sidebar activo
    document.querySelectorAll('.sidebar-nav .nav-item').forEach(item => {
      if (item.dataset.view === viewName) {
        item.classList.add('active');
      } else {
        item.classList.remove('active');
      }
    });

    // Actualizar badges
    const pacCount = await offlineStore.count('pacientes');
    const bPac = document.getElementById('badge-pacientes-count');
    if (bPac) bPac.textContent = pacCount;

    // Renderizar módulo con protección try/catch
    this.mainContent.innerHTML = '<div style="padding: 2rem; text-align: center; color: var(--text-muted);">Cargando módulo clínico...</div>';

    try {
      switch (viewName) {
        case 'pacientes':
          await renderPacientesView(this.mainContent, this.activePatientId, (pId) => {
            this.activePatientId = pId;
            this.renderHeader();
          });
          break;

        case 'hcd':
          await renderHCDView(this.mainContent, this.activePatientId);
          break;

        case 'escalas':
          await renderEscalasView(this.mainContent, this.activePatientId);
          break;

        case 'prestadores':
          await renderPrestadoresView(this.mainContent, this.activePatientId, (pId) => {
            this.activePatientId = pId;
            this.renderHeader();
          });
          break;

        case 'logistica':
          await renderLogisticaView(this.mainContent, this.activePatientId);
          break;

        case 'auditoria':
          await renderAuditoriaView(this.mainContent);
          break;

        case 'reportes':
          await renderReportesView(this.mainContent, this.activePatientId);
          break;

        default:
          this.mainContent.innerHTML = `<div class="card">Módulo ${viewName} en preparación.</div>`;
      }
    } catch (viewErr) {
      console.error(`Error al renderizar vista ${viewName}:`, viewErr);
      this.mainContent.innerHTML = `
        <div class="card" style="padding: 2rem; color: var(--danger-600); text-align: center;">
          <h3>Error al cargar el módulo ${viewName}</h3>
          <p style="margin-top: 0.5rem; color: var(--text-secondary);">${viewErr.message || viewErr}</p>
          <button class="btn btn-secondary btn-sm" onclick="window.domiCareApp.navigateTo('pacientes')" style="margin-top: 1rem;">Volver al Padrón</button>
        </div>
      `;
    }
  }

  updateSidebarUser() {
    const user = authState.getActiveUser();
    if (!user) return;

    const elAvatar = document.getElementById('sidebar-user-avatar');
    const elName = document.getElementById('sidebar-user-name');
    const elRole = document.getElementById('sidebar-user-role');
    const elSubtitle = document.getElementById('sidebar-app-subtitle');

    if (elName) elName.textContent = user.nombre;
    if (elRole) elRole.textContent = user.rol;
    if (elAvatar) {
      const parts = user.nombre.split(' ');
      const initials = parts.length > 1 ? parts[0][0] + parts[1][0] : parts[0].substring(0, 2);
      elAvatar.textContent = initials.toUpperCase();
    }

    const lic = licenseState.getLicenseInfo();
    if (elSubtitle) {
      if (licenseState.isLicensed() && lic.companyName) {
        elSubtitle.textContent = lic.companyName;
      } else {
        elSubtitle.textContent = 'Internación Profesional';
      }
    }
  }
}

// Inicialización instantánea y segura
function startApp() {
  if (!window.domiCareApp) {
    window.domiCareApp = new DomiCareApp();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startApp);
} else {
  startApp();
}
